import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { BroadcastChannelService } from '@services/broadcast-channel.service';
import { UserBehaviorService } from '@services/user-events/user-behavior.service';
import { LogoutService } from '@services/auth/logout.service';
import { AuthService } from '@services/auth/authService.service';
import { MsalBootstrapService } from '@services/auth/msal-bootstrap.service';

export interface dataStructure {
  id: number;
  username: string;
  email: string;
  picture: string;
  system: string;
}

@Component({
  selector: 'lib-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.css'],
  standalone: true,
  imports: [NgClass]
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() content!: any;

  username: string | undefined = '';
  email!: string | undefined;
  so: any;
  soTransform: any;
  soCapture: any;

  disableBtn: boolean = false;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private authService: MsalService,
    private appAuthService: AuthService,
    private msalBootstrapService: MsalBootstrapService,
    private broadcastChannelService: BroadcastChannelService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private logoutService: LogoutService
  ) {}

  async ngOnInit() {
    await this.msalBootstrapService.ensureReady(this.authService, this.appAuthService);
    const loggedByMsal = this.authService.instance.getActiveAccount();

    /** Check if the user is already logged in. */
    if (loggedByMsal) {
      this.email = loggedByMsal.username;
      this.username = loggedByMsal.name;
      this.so = window.navigator.userAgent;
      this.soTransform = this.so.split('(');
      this.soCapture = this.soTransform[1].split(';');

      this.userBehavior.setUser({
        name: this.username,
        email: this.email,
        from: 'msal'
      });

      const returnUrl = this.logoutService.getLogoutReason() !== 'manual'
        ? sessionStorage.getItem('logout_return_url')
        : null;

      if (returnUrl && returnUrl !== '/login') {
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigate(['/home']);
      }
    }
  }

  async login() {
    await this.authService.instance.initialize();
    this.authService
      .loginPopup()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: AuthenticationResult) => {
        if (response) {
          this.authService.instance.setActiveAccount(response.account);

          if (response.idToken) {
            this.appAuthService.setAzureIdToken(response.idToken);
          }

          this.broadcastChannelService.notifyLoginSuccess();

          this.userBehavior.setUser({
            name: response.account?.name ?? '',
            email: response.account?.username ?? '',
            from: 'msal'
          });

          this.router.navigate(['/home']);
        }
      });
  }

  /** Temporarily disable the button. */
  disableBtnTime() {
    this.disableBtn = true;

    setTimeout(() => {
      this.disableBtn = false;
    }, 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
